import {
  CreateRecordBatchRequest,
  CreateRecordBatchResponse,
  DynamoFile,
  ExtractlyPlusFile,
  FileRecord,
  FileStatus,
  GetFileType,
  ParseTableResult,
  RatioPageArea,
  Template,
  TestDataOcr,
  TextcutRectangle,
  UserSettings,
} from '../types'
import {
  fetchPDFFromLocal,
  findLocalFileRecordWithFileId,
  findLocalFileRecordWithFileIds,
  getLocalFileRecords,
  updateLocalFileRecords,
} from '../workbook'
import {
  addFileToDB,
  cacheFile,
  fileInDB,
  getFileFromDB,
  getFilesFromDB,
  removeFileFromDB,
} from '../utils/db'
import { breakDownOcr } from '../utils/ocr'
import { getDateBlocks } from '../utils/fuzzy'
import { createWorkerFactory } from '@shopify/web-worker'
import { apiClient, externalClient } from './client'
import { pipe } from 'fp-ts/function'
import * as T from 'fp-ts/Task'

const createWorker = createWorkerFactory(
  () => import('../workers/listFile_worker')
)

export const putFileToSignedURL = (url: string, file: File) =>
  externalClient.put(url, file, {
    headers: {
      'Content-Type': file.type,
    },
  })

export const checkSubscriptionStatus = async () => {
  const data = await apiClient.get<{
    data: {
      isSubscribed: boolean
      firmId: string
    }
  }>(`/check-subscription`)
  return data.data.data
}

export const startProcessingDoc = async (
  fileId: string,
  contentType: string
) => {
  const body = { fileId, contentType }
  const data = await apiClient.post<{
    message: string
  }>('/v1/file/process-doc', body)
  // console.log(data)
  return data.data.message
}

export const startProcessingRedactedDoc = async (
  fileId: string,
  contentType: string
) => {
  const body = { fileId, contentType }
  const data = await apiClient.post<{
    message: string
  }>('/v1/file/process-redacted-doc', body)
  return data.data.message
}

export const listAllFilesV2 = async () => {
  // const start = dayjs()
  const [remoteFileRecords, localFileRecords] = await getLocalFileRecords()
  if (!remoteFileRecords.length && !localFileRecords.length) return []

  const files = await fetchRemoteFiles(remoteFileRecords)

  const worker = createWorker()

  const localFiles = await worker.fetchLocalFiles(localFileRecords)

  const fileCacheStatus = await Promise.all(
    files
      .filter(
        (file) => file.type === 'FILE' && file.status === FileStatus.SUCCEEDED
      )
      .map(async (file) => {
        const count = await fileInDB(file.fileId)
        return [file, count] as const
      })
  )

  await Promise.all(
    fileCacheStatus
      .filter(([, count]) => count === 0)
      .map(async ([file, _]) => {
        const res = await apiClient.get<{ data: GetFileType }>(
          `/v1/file/${file.fileId}/${file.userId}`
        )
        const data = res.data.data
        await cacheFile(data)
      })
  )

  const records: FileRecord[] = files.map((file) => ({
    fileId: file.fileId,
    status: file.status,
    userId: file.userId,
  }))

  await updateLocalFileRecords(records)

  const cloudFiles: DynamoFile[] = files.map((file) => ({
    ...file,
    isLocal: false,
  }))
  const localFs: DynamoFile[] = localFiles.map((file) => ({
    ...file,
    isLocal: true,
  }))

  return [...cloudFiles, ...localFs]
}

export const listFilesByFilesIds = async (fileIds: string[]) =>
  pipe(
    listAllFilesV2,
    T.map((files) => files.filter((file) => fileIds.includes(file.fileId)))
  )()

const fetchRemoteFiles = async (remotesFileRecords: FileRecord[]) => {
  if (!remotesFileRecords.length) return []
  const body = remotesFileRecords.map((record) => ({
    fileId: record.fileId,
    userId: record.userId,
  }))

  const data = await apiClient.post<{ data: DynamoFile[] }>(
    '/v1/file/batch-get',
    body
  )
  return data.data.data
}

export const createRecord = (
  fileName: string,
  /** @deprecated in favor of `parentFolderId` */
  relativePath: string,
  contentType: string
) =>
  apiClient.post<{ data: { fileId: string } }>('/v1/file', {
    fileName,
    /** @deprecated in favor of `parentFolderId` */
    dir: relativePath,
    contentType,
  })

export const createRecordBatch = (
  files: CreateRecordBatchRequest['files']
): Promise<CreateRecordBatchResponse> =>
  apiClient.post('/v1/file/batch', { files }).then((res) => res.data)

export const getSignedUrl = (fileId: string, contentType: string) =>
  apiClient.post<{ uploadURL: string }>('/get-signed-url', {
    fileId,
    contentType,
  })

export const getFile = async (id: string): Promise<[Blob, TestDataOcr]> => {
  const file = await getFileFromDB(id)
  if (file) {
    return [file.pdf, file.ocr]
  }
  const res = await apiClient.get<{ data: GetFileType }>(`/v1/file/${id}`)
  const { pdfUrl, ocrUrl } = res.data.data
  const pdf = await fetch(pdfUrl)
  const pdfBlob = await pdf.blob()
  const ocr = await fetch(ocrUrl)
  const ocrJson = await ocr.json()
  return [pdfBlob, ocrJson]
}

export const getFileV2 = async (id: string): Promise<ExtractlyPlusFile> => {
  const record = await findLocalFileRecordWithFileId(id)
  if (!record) throw new Error('Internal Error')
  const file = await getFileFromDB(id)
  if (file)
    return {
      ...file,
      isLocal: record.isLocal,
    }

  if (record.isLocal) {
    const localFile = await fetchPDFFromLocal(record.fileId)
    if (!localFile)
      throw new Error(`Cannot fetch file from local, where fileId=${id}`)
    const [, , , lines, words] = breakDownOcr(localFile.ocr, 0)
    const blocks = [...lines.values(), ...words.values()]
    const dateBlocks = getDateBlocks(blocks)
    await addFileToDB({
      id: localFile.fileId,
      type: localFile.type,
      folderName: localFile.folderName,
      parentFolderId: localFile.parentFolderId,
      createdAt: new Date(),
      extractionMethod: localFile.extractionMethod,
      fileName: localFile.fileName,
      ocr: localFile.ocr,
      ocrDateCache: dateBlocks,
      pdf: localFile.pdfBlob,
      extractionEngine: localFile.extractionEngine,
    })
    return {
      id: localFile.fileId,
      type: localFile.type,
      folderName: localFile.folderName,
      parentFolderId: localFile.parentFolderId,
      createdAt: new Date(),
      fileName: localFile.fileName,
      ocr: localFile.ocr,
      pdf: localFile.pdfBlob,
      ocrDateCache: dateBlocks,
      extractionMethod: localFile.extractionMethod,
      isLocal: true,
    }
  }

  const res = await apiClient.get<{ data: GetFileType }>(
    `/v1/file/${id}/${record.userId}`
  )
  const data = res.data.data
  return cacheFile(data)
}

export const getFileBatchV2 = async (
  fileIds: string[]
): Promise<ExtractlyPlusFile[]> => {
  const records = await findLocalFileRecordWithFileIds(fileIds)

  if (records.some((r) => !r)) throw new Error('Internal Error')
  const files = await getFilesFromDB(fileIds)
  if (files.length === fileIds.length) return files

  const results: ExtractlyPlusFile[] = []
  for (const record of records) {
    const res = await apiClient.get<{ data: GetFileType }>(
      `/v1/file/${record.fileId}/${record.userId}`
    )
    const data = res.data.data
    const pdf = await (await fetch(data.pdfUrl)).blob()
    const ocr: TestDataOcr = await (await fetch(data.ocrUrl)).json()
    const [, , , lines, words] = breakDownOcr(ocr, 0)
    const blocks = [...lines.values(), ...words.values()]
    const dateBlocks = getDateBlocks(blocks)
    const extractlyPlusFile: ExtractlyPlusFile = {
      id: data.fileId,
      createdAt: new Date(),
      fileName: data.fileName,
      type: data.type,
      folderName: data.folderName,
      parentFolderId: data.parentFolderId,
      dir: data.dir,
      ocr,
      pdf,
      ocrDateCache: dateBlocks,
      extractionMethod: data.extractionMethod,
    }
    await addFileToDB(extractlyPlusFile)
    results.push(extractlyPlusFile)
  }
  return results
}

export const getS3FileInfo = async (id: string) => {
  const record = await findLocalFileRecordWithFileId(id)
  if (!record) throw new Error('Internal Error')
  const res = await apiClient.get<{ data: GetFileType }>(
    `/v1/file/${id}/${record.userId}`
  )
  return res.data.data
}

export const deleteFile = async (fileId: string, userId: string) => {
  await removeFileFromDB(fileId)
  return apiClient.delete<{ data: boolean; status: number }>(
    `/v1/file/${fileId}/${userId}`
  )
}

export const redactFile = async (
  redacts: TextcutRectangle[],
  fileId: string
) => {
  await removeFileFromDB(fileId)
  const file = await getS3FileInfo(fileId)
  const redactions = redacts.map((redact) => ({
    canvas_height: redact.ocrH,
    canvas_width: redact.ocrW,
    rect_height: redact.h,
    rect_width: redact.w,
    rect_in_x0: redact.x,
    rect_in_y0: redact.y,
    page_no: redact.filePage - 1,
  }))
  return apiClient.post(`${process.env.REACT_APP_REDACT_URL}`, {
    file_id: file.fileId,
    user_id: file.userId,
    input_pdf_s3_key: file.originalFileKey,
    redactions,
  })
}

export const trimPages = async (fileId: string, pagesToKeep: Array<number>) => {
  await removeFileFromDB(fileId)
  const body = { fileId, pagesToKeep }
  const data = await apiClient.post<{
    message: string
  }>('/v1/file/trim-pages', body)
  console.log(data)
  return data.data.message
}

export const parseTable = async (body: {
  fileId: string
  ratioPageArea?: RatioPageArea
}) => {
  const res = await apiClient.post<{
    success: boolean
    data: ParseTableResult[]
  }>(`/table/parse`, body)
  return res.data
}

export const listSuccessFiles = pipe(
  listAllFilesV2,
  T.map((files) =>
    files.filter(
      (file) => file.type === 'FILE' && file.status === FileStatus.SUCCEEDED
    )
  )
)

export const listSuccessFilesAndFolders = pipe(
  listAllFilesV2,
  T.map((files) =>
    files.filter(
      (file) => file.type === 'FOLDER' || file.status === FileStatus.SUCCEEDED
    )
  )
)

export const updateParentFolder = async (
  fileId: string,
  parentFolderId: string,
  userId: string
) => {
  const res = await apiClient.post(`/v1/file/update-parent`, {
    fileId,
    parentFolderId,
    userId,
  })
  return res.data
}

export const createFolder = async (
  parentFolderId: string | undefined,
  folderName: string
) => {
  const res = await apiClient.post<{
    data: { folderId: string }
  }>(`/v1/file/folder`, {
    parentFolderId,
    folderName,
  })
  return res.data.data.folderId
}

export const checkExpiryFiles = async () => {
  const [remoteFileRecords] = await getLocalFileRecords()
  if (!remoteFileRecords.length) return []
  const body = remoteFileRecords.map((record) => ({
    fileId: record.fileId,
    userId: record.userId,
  }))
  const res = await apiClient.post<{ data: string[] }>(
    `/v1/file/check-expiry`,
    body
  )
  return res.data.data
}

export const updateFileName = ({
  fileId,
  fileName,
  userId,
  isFile,
}: {
  fileId: string
  userId: string
  fileName: string
  isFile: boolean
}) => apiClient.put(`/v1/file/filename`, { fileId, fileName, userId, isFile })

export const getTemplates = () => {
  return apiClient.get<{ data: Template[]; message: string }>('/template')
}

export const saveTemplate = (template: Template) => {
  return apiClient.post<{ message: string }>('/template', template)
}

export const deleteTemplate = (templateId: string) =>
  apiClient.delete<{ message: string; error?: Error }>(
    `/template/${templateId}`
  )

export const updateTemplate = (template: Template) => {
  return apiClient.put<{ message: string }>(
    `/template/${template.templateId}`,
    template
  )
}

export const getUserSettings = () =>
  apiClient.get<{ data: UserSettings }>(`/settings`)

export const updateUserSettings = (settings: UserSettings) =>
  apiClient.put<{ data: UserSettings }>(`/settings`, settings)
